/**
 * AI Treatment Plan Revision via Medplum Bot (`ai-treatment-plan-revision`).
 */

import { ContentType } from '@medplum/core';
import type { MedplumClient } from '@medplum/core';
import type { Identifier, Questionnaire, QuestionnaireResponse } from '@medplum/fhirtypes';
import type { TreatmentPlanRevisionSuggestion } from './treatmentPlanAiRevision';

export const AI_TREATMENT_PLAN_REVISION_BOT_IDENTIFIER = {
  system: 'https://unify.health/bots',
  value: 'ai-treatment-plan-revision',
} as const;

export function getAiTreatmentPlanRevisionBotExecuteTarget(): string | Identifier {
  const id = import.meta.env.VITE_MEDPLUM_AI_TREATMENT_PLAN_REVISION_BOT_ID?.trim();
  if (id) return id;
  const system =
    import.meta.env.VITE_MEDPLUM_AI_TREATMENT_PLAN_REVISION_BOT_IDENTIFIER_SYSTEM?.trim()
    ?? AI_TREATMENT_PLAN_REVISION_BOT_IDENTIFIER.system;
  const value =
    import.meta.env.VITE_MEDPLUM_AI_TREATMENT_PLAN_REVISION_BOT_IDENTIFIER_VALUE?.trim()
    ?? AI_TREATMENT_PLAN_REVISION_BOT_IDENTIFIER.value;
  return { system, value };
}

export interface RequestAiTreatmentPlanRevisionInput {
  patientId: string;
  episodeId?: string;
  prompt: string;
  questionnaireResponseId?: string;
  questionnaire?: Questionnaire;
  questionnaireResponse?: QuestionnaireResponse;
}

export async function requestAiTreatmentPlanRevision(
  medplum: MedplumClient,
  input: RequestAiTreatmentPlanRevisionInput,
): Promise<TreatmentPlanRevisionSuggestion> {
  const result = await medplum.executeBot(
    getAiTreatmentPlanRevisionBotExecuteTarget(),
    input,
    ContentType.JSON,
  );
  if (!result || typeof result !== 'object') {
    throw new Error('AI treatment plan revision bot did not return a result');
  }
  const suggestion = result as TreatmentPlanRevisionSuggestion;
  if (typeof suggestion.summary !== 'string' || !Array.isArray(suggestion.operations)) {
    throw new Error('AI treatment plan revision bot returned an invalid suggestion payload');
  }
  return suggestion;
}
